import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchIntakesByClientId } from "../../store/thunks/intakeThunk";
import IntakeDetailsModal from "../intakesPage/IntakeDetailsModal";
import { 
  DocumentTextIcon, 
  ClockIcon, 
  CheckCircleIcon, 
  ExclamationCircleIcon,
  XCircleIcon,
  EyeIcon,
  CalendarIcon,
  UserIcon,
  ChevronRightIcon
} from "@heroicons/react/24/outline";

const ClientIntake = () => {
  const dispatch = useDispatch();
  const [selectedIntake, setSelectedIntake] = useState(null);
  const client = useSelector((state) => state.clients.selectedClient);
  const [clientIntakes, setClientIntakes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Helper function to get status color and icon
  const getStatusDisplay = (status) => {
    const statusMap = {
      'completed': {
        color: 'text-green-600 bg-green-50 border-green-200',
        icon: CheckCircleIcon,
        label: 'Completed'
      },
      'in-progress': {
        color: 'text-yellow-600 bg-yellow-50 border-yellow-200',
        icon: ClockIcon,
        label: 'In Progress'
      },
      'pending': {
        color: 'text-blue-600 bg-blue-50 border-blue-200',
        icon: ExclamationCircleIcon,
        label: 'Pending'
      },
      'draft': {
        color: 'text-gray-600 bg-gray-50 border-gray-200',
        icon: DocumentTextIcon,
        label: 'Draft'
      },
      'cancelled': {
        color: 'text-red-600 bg-red-50 border-red-200',
        icon: XCircleIcon,
        label: 'Cancelled'
      }
    };
    
    return statusMap[status.toLowerCase()] || statusMap.pending;
  };

  useEffect(() => {
    const fetchIntakes = async () => {
      if (client?._id) {
        setLoading(true);
        try {
          const result = await dispatch(fetchIntakesByClientId(client._id)).unwrap();
          setClientIntakes(result);
        } catch (error) {
          console.error("Error fetching intakes:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchIntakes();
  }, [client?._id, dispatch]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="animate-pulse">
              <div className="h-6 w-6 bg-gray-200 rounded"></div>
            </div>
            <div className="animate-pulse flex-1">
              <div className="h-6 bg-gray-200 rounded w-32"></div>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="h-4 bg-gray-200 rounded w-1/3"></div>
              <div className="h-3 bg-gray-200 rounded w-1/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!clientIntakes || clientIntakes.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <DocumentTextIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Intake Forms</h2>
              <p className="text-sm text-gray-600 mt-1">
                View and manage client intake information
              </p>
            </div>
          </div>
        </div>
        
        {/* Empty State */}
        <div className="p-12">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gray-100">
              <DocumentTextIcon className="h-8 w-8 text-gray-400" />
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">No intake forms available</h3>
            <p className="mt-2 text-sm text-gray-500 max-w-sm mx-auto">
              No intake forms have been submitted for this client yet. Intake forms will appear here once they are completed.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <DocumentTextIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">Intake Forms</h2>
              <p className="text-sm text-gray-600 mt-1">
                View and manage client intake information
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800">
              {clientIntakes.length} {clientIntakes.length === 1 ? 'form' : 'forms'}
            </span>
          </div>
        </div>
      </div>

      {/* Intake Forms List */}
      <div className="p-6">
        <div className="space-y-4">
          {clientIntakes.map((intake, index) => {
            const latestStatus = intake.statusHistory[intake.statusHistory.length - 1];
            const statusDisplay = getStatusDisplay(latestStatus.status);
            const StatusIcon = statusDisplay.icon;
            
            return (
              <div
                key={intake._id || index}
                className="group relative border border-gray-200 rounded-lg p-6 cursor-pointer hover:border-indigo-300 hover:shadow-md transition-all duration-200 bg-gradient-to-r from-white to-gray-50/30"
                onClick={() => setSelectedIntake(intake)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    {/* Form Icon */}
                    <div className="flex-shrink-0">
                      <div className="h-12 w-12 rounded-lg bg-indigo-100 flex items-center justify-center group-hover:bg-indigo-200 transition-colors">
                        <DocumentTextIcon className="h-6 w-6 text-indigo-600" />
                      </div>
                    </div>
                    
                    {/* Form Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-3 mb-3">
                        <h3 className="text-lg font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
                          Intake Form #{index + 1}
                        </h3>
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${statusDisplay.color}`}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {statusDisplay.label}
                        </span>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div className="flex items-center space-x-2 text-gray-600">
                          <CalendarIcon className="h-4 w-4 text-gray-400" />
                          <span>Last Updated:</span>
                          <span className="font-medium text-gray-900">
                            {new Date(latestStatus.modifiedAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                        
                        {latestStatus.modifiedBy && (
                          <div className="flex items-center space-x-2 text-gray-600">
                            <UserIcon className="h-4 w-4 text-gray-400" />
                            <span>Modified by:</span>
                            <span className="font-medium text-gray-900">
                              {latestStatus.modifiedBy.firstName} {latestStatus.modifiedBy.lastName}
                            </span>
                          </div>
                        )}
                        
                        <div className="flex items-center space-x-2 text-gray-600">
                          <ClockIcon className="h-4 w-4 text-gray-400" />
                          <span>Created:</span>
                          <span className="font-medium text-gray-900">
                            {new Date(intake.createdAt).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                        
                        {intake.completedSections && (
                          <div className="flex items-center space-x-2 text-gray-600">
                            <DocumentTextIcon className="h-4 w-4 text-gray-400" />
                            <span>Progress:</span>
                            <span className="font-medium text-gray-900">
                              {Object.keys(intake.completedSections).length} sections completed
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  {/* Action Button */}
                  <div className="flex-shrink-0 ml-4">
                    <div className="flex items-center space-x-2">
                      <div className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 group-hover:border-indigo-300 group-hover:text-indigo-600 transition-colors">
                        <EyeIcon className="h-4 w-4 mr-1" />
                        View Details
                      </div>
                      <ChevronRightIcon className="h-5 w-5 text-gray-400 group-hover:text-indigo-500 transition-colors" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modal */}
      {selectedIntake && (
        <IntakeDetailsModal
          intake={selectedIntake}
          onClose={() => setSelectedIntake(null)}
        />
      )}
    </div>
  );
};

export default ClientIntake; 